import { apiClient } from '@/api/client';
import { useAuthContext } from '@/components/auth';
import type { AvatarKey } from '@/constants/avatars';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { profileKeys } from './useProfile';

/**
 * ============================================
 * Child Profile Hooks
 * ============================================
 *
 * Hooks for child profile management:
 * - useCurrentChild: Get current (default) child
 * - useChildList: Get all children (multi-child)
 * - useCreateChild: Create a new child profile
 * - useUpdateChild: Update a child profile
 * - useDeleteChild: Delete a child profile
 * - useSetDefaultChild: Set a child as default
 * - useOnboarding: Check if onboarding is needed
 */

// Re-export AvatarKey for convenience
export type { AvatarKey };

// ============================================
// Types
// ============================================

/**
 * Child Grade (Singapore Primary School)
 */
export type ChildGrade = 'P1' | 'P2' | 'P3';

/**
 * Grade Labels
 */
export const GRADE_LABELS: Record<ChildGrade, string> = {
  P1: 'Primary 1',
  P2: 'Primary 2',
  P3: 'Primary 3',
};

/**
 * Grade Options for selectors
 */
export const GRADE_OPTIONS: { value: ChildGrade; label: string }[] = [
  { value: 'P1', label: 'Primary 1' },
  { value: 'P2', label: 'Primary 2' },
  { value: 'P3', label: 'Primary 3' },
];

/**
 * Child Profile
 */
export interface ChildProfile {
  id: string;
  nickname: string;
  avatar: AvatarKey;
  grade: ChildGrade;
  curriculum: string;
  preferredLanguage: string;
  isDefault: boolean;
  createdAt: string;
}

/**
 * Child Profile List Item (with statistics)
 */
export interface ChildProfileListItem extends ChildProfile {
  taskCount: number;
}

/**
 * Create Child Request
 */
export interface CreateChildRequest {
  nickname: string;
  grade: ChildGrade;
  avatar?: AvatarKey;
}

/**
 * Update Child Request
 */
export interface UpdateChildRequest {
  nickname?: string;
  grade?: ChildGrade;
  avatar?: AvatarKey;
}

/**
 * API Success Response wrapper
 */
interface SuccessResponse<T> {
  success: boolean;
  data: T;
}

// ============================================
// API Functions
// ============================================

/**
 * Fetch current (default) child
 */
async function fetchCurrentChild(): Promise<SuccessResponse<ChildProfile | null>> {
  const response = await apiClient.get<SuccessResponse<ChildProfile | null>>('/children/current');
  return response.data;
}

/**
 * Fetch all children
 */
async function fetchChildren(): Promise<SuccessResponse<ChildProfileListItem[]>> {
  const response = await apiClient.get<SuccessResponse<ChildProfileListItem[]>>('/children');
  return response.data;
}

/**
 * Create a child profile
 */
async function createChild(data: CreateChildRequest): Promise<SuccessResponse<ChildProfile>> {
  const response = await apiClient.post<SuccessResponse<ChildProfile>>('/children', data);
  return response.data;
}

/**
 * Update a child profile
 */
async function updateChild(
  id: string,
  data: UpdateChildRequest
): Promise<SuccessResponse<ChildProfile>> {
  const response = await apiClient.patch<SuccessResponse<ChildProfile>>(`/children/${id}`, data);
  return response.data;
}

/**
 * Delete a child profile
 */
async function deleteChild(id: string): Promise<SuccessResponse<{ message: string }>> {
  // Fastify requires body when Content-Type is application/json
  const response = await apiClient.delete<SuccessResponse<{ message: string }>>(`/children/${id}`, {
    data: {},
  });
  return response.data;
}

/**
 * Set a child as default
 */
async function setDefaultChild(id: string): Promise<SuccessResponse<ChildProfile>> {
  // Fastify requires body when Content-Type is application/json
  const response = await apiClient.post<SuccessResponse<ChildProfile>>(
    `/children/${id}/default`,
    {}
  );
  return response.data;
}

// ============================================
// Query Keys
// ============================================

export const childKeys = {
  all: ['children'] as const,
  lists: () => [...childKeys.all, 'list'] as const,
  current: () => [...childKeys.all, 'current'] as const,
  detail: (id: string) => [...childKeys.all, 'detail', id] as const,
};

// ============================================
// Hooks
// ============================================

/**
 * Hook for fetching current (default) child
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useCurrentChild();
 *
 * if (data?.data) {
 *   console.log(data.data.nickname);
 * }
 * ```
 */
export function useCurrentChild(options?: { enabled?: boolean }) {
  const { canMakeAuthenticatedRequests } = useAuthContext();

  const enabled = canMakeAuthenticatedRequests && (options?.enabled ?? true);

  return useQuery({
    queryKey: childKeys.current(),
    queryFn: fetchCurrentChild,
    enabled,
  });
}

/**
 * Hook for fetching all children (multi-child support)
 */
export function useChildList(options?: { enabled?: boolean }) {
  const { canMakeAuthenticatedRequests } = useAuthContext();

  const enabled = canMakeAuthenticatedRequests && (options?.enabled ?? true);

  return useQuery({
    queryKey: childKeys.lists(),
    queryFn: fetchChildren,
    enabled,
  });
}

/**
 * Hook for creating a child profile
 *
 * Uses setQueryData for immediate cache update to prevent
 * onboarding redirect loop issues.
 */
export function useCreateChild() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createChild,
    onSuccess: (data) => {
      // 立即更新 current child 缓存 - 防止 onboarding 重定向循环
      queryClient.setQueryData(childKeys.current(), data);

      // 更新 children list 缓存
      queryClient.setQueryData(
        childKeys.lists(),
        (old: SuccessResponse<ChildProfileListItem[]> | undefined) => {
          if (!old) return { success: true, data: [{ ...data.data, taskCount: 0 }] };
          return {
            ...old,
            data: [...old.data, { ...data.data, taskCount: 0 }],
          };
        }
      );

      // 后台刷新确保数据最新
      queryClient.invalidateQueries({ queryKey: childKeys.all });
      queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
  });
}

/**
 * Hook for updating a child profile
 */
export function useUpdateChild() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateChildRequest }) => updateChild(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: childKeys.all });
      queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
  });
}

/**
 * Hook for deleting a child profile
 */
export function useDeleteChild() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteChild,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: childKeys.all });
      queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
  });
}

/**
 * Hook for setting a child as default
 *
 * @example
 * ```tsx
 * const { mutate: setDefault, isPending } = useSetDefaultChild();
 *
 * setDefault(childId);
 * ```
 */
export function useSetDefaultChild() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: setDefaultChild,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: childKeys.all });
      queryClient.invalidateQueries({ queryKey: profileKeys.me() });
    },
  });
}

/**
 * Hook for checking if onboarding is needed
 *
 * Returns true if user has no child profile and needs to create one
 *
 * @example
 * ```tsx
 * const { needsOnboarding, isLoading } = useOnboarding();
 *
 * if (needsOnboarding) {
 *   navigate("/onboarding");
 * }
 * ```
 */
export function useOnboarding() {
  const { data, isLoading, error } = useCurrentChild();

  return {
    /** Whether user needs to complete onboarding (no child profile) */
    needsOnboarding: !isLoading && !error && data?.data === null,
    /** Whether check is still loading */
    isLoading,
    /** Current child if exists */
    currentChild: data?.data ?? null,
    /** Error if any */
    error,
  };
}
